import { reloadPage } from './reload';

// BUG-054 · **A BUILD THAT WAS REPLACED UNDER AN OPEN TAB MUST HEAL ITSELF.**
//
// Every route but Welcome is a separate chunk (`app/router.tsx`, ARCH §9.1), and a chunk is requested by the
// hashed name the CURRENTLY LOADED `index-*.js` was built with. Replace the folder that serves the app —
// a deploy, or the founder's preview being refreshed under his open tab — and every one of those names is
// gone. The tab keeps working until the reader clicks a link; then the import fails, and because nothing
// caught it, EVERY route landed in the error boundary reading "Something went wrong on this page". The app
// was not broken. It was one reload out of date, and it never said so.
//
// Vite fires `vite:preloadError` on `window` when a preloaded chunk cannot be fetched. Calling
// `preventDefault()` there stops Vite's own default (rethrow) and hands us the decision: reload once.
//
// **Once** is the whole design. A reload that does not help — the chunk is genuinely missing from the server,
// not merely renamed — must not become a loop that reloads for ever in front of somebody trying to read
// their spend. The attempt is remembered in `sessionStorage` against the identity of the build that made it,
// so: a stale build gets exactly one silent reload; the build it reloads INTO gets its own fresh attempt (it
// is a different `index-*.js`, so a different key); and a second failure on the same build falls through to
// the boundary, which says what happened in words and offers the button.
//
// `sessionStorage` and not `localStorage`: the fact is about THIS tab's session, and a blocked or full store
// must never stop the app from loading — every access is wrapped.

/** The `sessionStorage` key: one entry per build, so a new build is never refused its own first attempt. */
export const RELOAD_FLAG_PREFIX = 'sl.reloadedForChunk';

/**
 * What identifies the build this document is running. The entry script's `src` carries the content hash
 * (`/assets/index-B2XUvrDj.js`), which is exactly the thing that changes when the folder is swapped;
 * `import.meta.url` is the same file's URL and is what a bundle-less dev server has instead.
 */
export function buildKey(documentRef: Document = document, moduleUrl?: string): string {
  const entry = documentRef.querySelector<HTMLScriptElement>('script[type="module"][src]');
  const src = entry?.getAttribute('src') ?? moduleUrl ?? 'unknown-build';
  return `${RELOAD_FLAG_PREFIX}:${src}`;
}

function readFlag(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    // A blocked store means we cannot remember the attempt — so we never make one. A missing chunk shows
    // the boundary's own sentence instead, which is honest and, unlike a reload loop, stoppable.
    return true;
  }
}

function writeFlag(key: string): boolean {
  try {
    window.sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * The messages a browser gives a failed dynamic import. There is no error CODE for this, on any engine, so
 * the text is all there is — Chromium and Edge say "Failed to fetch dynamically imported module", Firefox
 * "error loading dynamically imported module", Safari "Importing a module script failed". A `TypeError` is
 * what all three throw.
 */
const CHUNK_ERROR =
  /dynamically imported module|Failed to fetch|Importing a module script failed|error loading dynamically imported/i;

/**
 * Is this the error of a chunk that is no longer on the server? Used by the route error boundary, which sees
 * the failure as a thrown value rather than as an event (a `lazy` route that rejects).
 *
 * Deliberately narrow: only a `TypeError` (or Vite's own event payload) with one of those messages. An
 * `ApiError`, a 404 route response and a genuine render crash all keep their own screens.
 */
export function isStaleBuildError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const candidate = error as { name?: unknown; message?: unknown; payload?: unknown };
  // `vite:preloadError` carries the original error on `payload`.
  if (candidate.payload !== undefined && isStaleBuildError(candidate.payload)) return true;
  if (typeof candidate.message !== 'string') return false;
  const isTypeError = candidate.name === 'TypeError' || error instanceof TypeError;
  return isTypeError && CHUNK_ERROR.test(candidate.message);
}

/**
 * Subscribers to "this build is out of date and reloading did not help". `App` is the only one in
 * production: a missing route file does not always reach the route error boundary — on a static host an
 * absent `assets/route-*.js` is answered with `index.html`, the browser refuses the MIME type, and React
 * Router renders an empty outlet with a console warning instead of throwing. That blank page inside a
 * working shell is what "mostly non functional" looked like, so the guard — which always hears the event —
 * is what the screen listens to.
 */
const listeners = new Set<() => void>();

/** `true` once a failure has arrived that a reload cannot fix (the flag for this build was already set). */
let staleBuildSeen = false;

export function isStaleBuildSeen(): boolean {
  return staleBuildSeen;
}

export function subscribeStaleBuild(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test-only: forget what this module saw, so one test's stale build is not the next one's. */
export function resetStaleBuildSeen(): void {
  staleBuildSeen = false;
}

function announceStaleBuild(): void {
  if (staleBuildSeen) return;
  staleBuildSeen = true;
  for (const listener of listeners) listener();
}

export interface StaleBuildGuardOptions {
  /** For tests: the document whose entry script names the build. */
  document?: Document;
  /** `import.meta.url` from the caller — the fallback identity when there is no bundled entry script. */
  moduleUrl?: string;
  /** For tests: the reload seam (`app/reload.ts` in production). */
  reload?: () => void;
}

/**
 * Listen for the missing chunk and reload once. Call it BEFORE the router mounts, so a failure during the
 * very first navigation is caught too.
 *
 * Returns the listener's own remover, which nothing in the app uses (the guard lives as long as the
 * document) and every test does.
 */
export function installStaleBuildReload(options: StaleBuildGuardOptions = {}): () => void {
  const key = buildKey(options.document ?? document, options.moduleUrl);
  const reload = options.reload ?? reloadPage;

  const onPreloadError = (event: Event) => {
    // Already tried on THIS build: the reload did not help, so the reader gets the words and the button
    // rather than a second reload. This is the branch that turns a would-be loop into one sentence — and
    // the announcement is what puts the sentence on screen even when the router renders an empty outlet
    // instead of throwing (see `announceStaleBuild`).
    if (readFlag(key)) {
      announceStaleBuild();
      return;
    }
    if (!writeFlag(key)) {
      announceStaleBuild();
      return;
    }
    event.preventDefault();
    reload();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
